// Fase 17.3, Plano 09 (D-20) — "Importar área e peças por gancheira": upload (.xlsx ou .csv) → prévia por linha (sem gravar)
// → confirmação → CSV das linhas com erro. A confirmação relê o arquivo no servidor; o navegador nunca envia o resultado da prévia.
import { useState } from 'react';
import type { PreviaDadosTecnicosDto, ResultadoDadosTecnicosDto } from '@pieltec/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { baixarTexto, lerArquivo, type ArquivoLido } from '../../../lib/roteiros/importacao-cargamaquina';
import {
  confirmarDadosTecnicos,
  linhasComErroParaCsv,
  previaDadosTecnicos,
  ROTULO_CASOU_POR,
  ROTULO_SITUACAO_LINHA,
  textoConfirmacaoDadosTecnicos,
} from '../../../lib/roteiros/importacao-dados-tecnicos';
import { SeletorArquivo } from './seletor-arquivo';

function Totais({ rotulo, valor }: { readonly rotulo: string; readonly valor: number }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border p-4">
      <span className="text-sm text-muted-foreground">{rotulo}</span>
      <span className="text-[28px] font-semibold leading-tight">{valor}</span>
    </div>
  );
}

function valorOuTraco(valor: string | number | null): string {
  return valor === null ? '—' : String(valor);
}

function mudanca(atual: string | number | null, nova: string | number | null): string {
  return nova === null ? valorOuTraco(atual) : `${valorOuTraco(atual)} → ${nova}`;
}

function nomeArquivoCsv(origem: string): string {
  const base = origem.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '-');
  return `linhas-com-erro-${base || 'importacao'}.csv`;
}

export function ImportarDadosTecnicosPage() {
  const [arquivo, setArquivo] = useState<File | null>(null);
  // Nome + conteúdo do arquivo da prévia, lidos juntos: a confirmação usa exatamente o que a prévia mostrou (P3-WR-03).
  const [lido, setLido] = useState<ArquivoLido | null>(null);
  const [previa, setPrevia] = useState<PreviaDadosTecnicosDto | null>(null);
  const [resultado, setResultado] = useState<ResultadoDadosTecnicosDto | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function escolher(escolhido: File | null): void {
    setArquivo(escolhido);
    setLido(null);
    setPrevia(null);
    setResultado(null);
    setErro(null);
  }

  async function gerarPrevia(): Promise<void> {
    if (!arquivo) return;
    setOcupado(true);
    setErro(null);
    setResultado(null);
    try {
      const lidoAgora = await lerArquivo(arquivo);
      setPrevia(await previaDadosTecnicos({ arquivoNome: lidoAgora.nome, arquivoBase64: lidoAgora.base64 }));
      setLido(lidoAgora);
    } catch (falha) {
      setPrevia(null);
      setErro(falha instanceof Error ? falha.message : 'Não foi possível gerar a prévia.');
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (!lido || !previa) return;
    setConfirmando(false);
    setOcupado(true);
    setErro(null);
    try {
      setResultado(await confirmarDadosTecnicos({ arquivoNome: lido.nome, arquivoBase64: lido.base64 }));
      setPrevia(null);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível confirmar a importação.');
    } finally {
      setOcupado(false);
    }
  }

  const podeConfirmar = previa !== null && previa.resumo.atualiza > 0 && !ocupado;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <h1 className="text-[28px] font-semibold leading-tight">Importar área e peças por gancheira</h1>
          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
            <p>Envie uma planilha (.xlsx ou .csv) com as colunas: Código; Área (m²); Peças por gancheira.</p>
            <p>Célula em branco não altera o valor atual.</p>
            <p>Até 5.000 linhas por arquivo; para listas maiores, divida em vários arquivos e importe em lotes.</p>
            <p>O código pode ser o da peça ou um código legado do CargaMáquina. A prévia mostra o que muda em cada linha; nada é gravado até você confirmar.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <SeletorArquivo aceita=".xlsx,.csv" rotulo="Planilha de área e peças por gancheira" desabilitado={ocupado} onEscolher={escolher} />
            <Button type="button" disabled={!arquivo || ocupado} onClick={() => void gerarPrevia()}>Gerar prévia</Button>
          </div>
          {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
        </CardContent>
      </Card>

      {resultado ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold">Importação concluída</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <Totais rotulo="Peças atualizadas" valor={resultado.pecasAtualizadas} />
              <Totais rotulo="Sem alteração" valor={resultado.semAlteracao} />
              <Totais rotulo="Linhas com erro" valor={resultado.linhasComErro.length} />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                type="button"
                variant="outline"
                disabled={resultado.linhasComErro.length === 0}
                onClick={() => baixarTexto(nomeArquivoCsv(lido?.nome ?? arquivo?.name ?? ''), linhasComErroParaCsv(resultado.linhasComErro))}
              >
                Baixar linhas com erro (CSV)
              </Button>
              <a href="/pcp/roteiros" className="text-sm underline">Ver peças</a>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {previa ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Totais rotulo="Total" valor={previa.resumo.total} />
              <Totais rotulo="Atualiza" valor={previa.resumo.atualiza} />
              <Totais rotulo="Sem alteração" valor={previa.resumo.semAlteracao} />
              <Totais rotulo="Erros" valor={previa.resumo.erros} />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button type="button" disabled={!podeConfirmar} onClick={() => setConfirmando(true)}>Confirmar</Button>
              <Button
                type="button"
                variant="outline"
                disabled={previa.resumo.erros === 0}
                onClick={() => baixarTexto(nomeArquivoCsv(lido?.nome ?? arquivo?.name ?? ''), linhasComErroParaCsv(previa.linhas.filter((linha) => linha.situacao === 'ERRO')))}
              >
                Baixar linhas com erro (CSV)
              </Button>
              {previa.resumo.atualiza === 0 ? <span className="text-sm text-muted-foreground">Nada para atualizar neste arquivo.</span> : null}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  {['Linha', 'Código', 'Peça', 'Casou por', 'Área (m²)', 'Peças por gancheira', 'Situação', 'Erro'].map((coluna) => (
                    <th key={coluna} className="py-2 pr-4 font-semibold">{coluna}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previa.linhas.map((linha) => (
                  <tr key={linha.linha}>
                    <td className="py-1 pr-4">{linha.linha}</td>
                    <td className="py-1 pr-4">{linha.codigo}</td>
                    <td className="py-1 pr-4">{linha.materialCodigo ?? '—'}</td>
                    <td className="py-1 pr-4">{linha.casouPor ? ROTULO_CASOU_POR[linha.casouPor] : '—'}</td>
                    <td className="py-1 pr-4">{mudanca(linha.areaAtual, linha.areaNova)}</td>
                    <td className="py-1 pr-4">{mudanca(linha.pecasPorGancheiraAtual, linha.pecasPorGancheiraNova)}</td>
                    <td className="py-1 pr-4">{ROTULO_SITUACAO_LINHA[linha.situacao]}</td>
                    <td className="py-1 pr-4 text-destructive">{linha.erro ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      {previa ? (
        <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar importação?</AlertDialogTitle>
              <AlertDialogDescription>
                {textoConfirmacaoDadosTecnicos(previa.resumo.atualiza)} {previa.resumo.erros} linha(s) com erro não serão aplicadas.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction onClick={() => void confirmar()}>Importar agora</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}
